import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import {
  createBrowserRouter, redirect, RouterProvider
} from "react-router-dom";
import App from './App';
import { Links } from './constants';
import { loadAdminAnalyticsPageData, loadAdminCalendarData, loadAdminInspirationHubPageData, loadAdminPostPageData, loadAdminSavePostData, loadAdminUsersPageData } from './loaders/admin';
import { loadAdminChannelsPageData } from './loaders/admin_channels_page';
import { loadAdminCampaignsPageData } from './loaders/admin_campaigns_page';
import { loadAdminCommentsPageData, loadAdminSaveCommentData } from './loaders/admin_comments_page';
import { loadAdminPostsPageData } from './loaders/admin_posts_page';
import { loadAdvocateAnalyticsPageData, loadAdvocateCalendarData, loadSharedCalendarData, loadAdvocateCommentsPageData, loadAdvocatePostsPageData, loadApprovePostPageData, loadExternalSharePostPageData, loadExternalSharePostPageResumeData, loadSharePostPageData, loadSocialMediaShareDetailsPageData } from './loaders/advocate_posts_page';
import { loadAppContext, loadSignInPageData } from './loaders/app_context';
import { loadPublicCampaignPageData, loadPublicCampaignPageResumeData } from './loaders/public_campaign_page';
import { Admin } from './pages/admin';
import { ErrorPage } from './pages/error';
import { NotFoundPage } from './pages/not-found';
import reportWebVitals from './reportWebVitals';
import * as serviceWorker from './serviceWorker';

// Pages are split into their own chunks; the router loads a page's chunk in parallel with its loader.
const lazyPage = (pageModule) => pageModule.then((m) => ({ Component: m.default }));

const container = document.getElementById('root');
const root = ReactDOM.createRoot(container);

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    loader: loadAppContext,
    errorElement: <ErrorPage />,
    children: [{
      errorElement: <ErrorPage />,
      children: [{
        loader: loadSignInPageData,
        path: Links.signInPagePath,
        lazy: () => lazyPage(import('./pages/sign-in'))
      }, {
        index: true,
        loader: () => redirect("/posts?from=root", { replace: true }),
      }, {
        path: "s/:shareTokenID",
        lazy: () => lazyPage(import('./pages/advocate/external-share-post')),
        loader: loadExternalSharePostPageData,
      }, {
        path: "s/:shareTokenID/resume",
        lazy: () => lazyPage(import('./pages/advocate/external-share-post')),
        loader: loadExternalSharePostPageResumeData,
      }, {
        path: "c/:campaignToken",
        lazy: () => lazyPage(import('./pages/advocate/campaign')),
        loader: loadPublicCampaignPageData,
      }, {
        path: "c/:campaignToken/resume",
        lazy: () => lazyPage(import('./pages/advocate/campaign')),
        loader: loadPublicCampaignPageResumeData,
      },
      {
        loader: loadAdvocatePostsPageData,
        path: "posts",
        lazy: () => lazyPage(import('./pages/advocate/posts')),
      }, {
        path: "posts/:postID/share",
        lazy: () => lazyPage(import('./pages/advocate/share-post')),
        loader: loadSharePostPageData,
      }, {
        path: "posts/:postID/approve",
        lazy: () => lazyPage(import('./pages/advocate/approve-post')),
        loader: loadApprovePostPageData,
      }, {
        path: "social-media-shares/:shareID",
        lazy: () => lazyPage(import('./pages/advocate/social-media-share-details')),
        loader: loadSocialMediaShareDetailsPageData,
      }, {
        path: "calendar",
        lazy: () => lazyPage(import('./pages/advocate/calendar')),
        loader: loadAdvocateCalendarData,
      }, {
        path: "calendars/:type/:id",
        lazy: () => lazyPage(import('./pages/advocate/shared-calendar')),
        loader: loadSharedCalendarData,

      }, {
        path: "comments",
        lazy: () => lazyPage(import('./pages/advocate/comments')),
        loader: loadAdvocateCommentsPageData,
      }, {
        path: "settings",
        lazy: () => lazyPage(import('./pages/advocate/settings')),
      }, {
        path: "analytics",
        lazy: () => lazyPage(import('./pages/advocate/analytics')),
        loader: loadAdvocateAnalyticsPageData,
      }, {
        path: "not-found",
        element: <NotFoundPage />
      },
      {
        path: "admin",
        element: <Admin />,
        children: [{
          index: true,
          loader: () => redirect("/admin/posts", { replace: true }),
        }, {
          path: "posts",
          children: [
            {
              index: true,
              lazy: () => lazyPage(import('./pages/admin/posts')),
              loader: loadAdminPostsPageData,
            },
            {
              path: "new",
              lazy: () => lazyPage(import('./pages/admin/save-post')),
              loader: loadAdminSavePostData,
            },
            {
              path: ":postID",
              lazy: () => lazyPage(import('./pages/admin/post')),
              loader: loadAdminPostPageData,
            },
            {
              path: ":postID/edit",
              lazy: () => lazyPage(import('./pages/admin/save-post')),
              loader: loadAdminSavePostData,
            },

          ]
        }, {
          path: "comments",
          children: [
            {
              index: true,
              lazy: () => lazyPage(import('./pages/admin/comments')),
              loader: loadAdminCommentsPageData,
            },
            {
              path: "new",
              lazy: () => lazyPage(import('./pages/admin/save-comment')),
              loader: loadAdminSaveCommentData,
            },
            {
              path: ":commentID/edit",
              lazy: () => lazyPage(import('./pages/admin/save-comment')),
              loader: loadAdminSaveCommentData,
            },
          ]
        }, {
          path: "settings",
          lazy: () => lazyPage(import('./pages/admin/settings')),
        }, {
          path: "channels",
          lazy: () => lazyPage(import('./pages/admin/channels')),
          loader: loadAdminChannelsPageData,
        }, {
          path: "campaigns",
          lazy: () => lazyPage(import('./pages/admin/campaigns')),
          loader: loadAdminCampaignsPageData,
        }, {
          path: "calendar",
          lazy: () => lazyPage(import('./pages/admin/calendar')),
          loader: loadAdminCalendarData,
        }, {
          path: "media",
          lazy: () => lazyPage(import('./pages/admin/media')),
        }, {
          path: "analytics",
          lazy: () => lazyPage(import('./pages/admin/analytics/analytics')),
          loader: loadAdminAnalyticsPageData,
        }, {
          path: "users",
          lazy: () => lazyPage(import('./pages/admin/users')),
          loader: loadAdminUsersPageData,
        }, {
          path: "inspiration-hub",
          lazy: () => lazyPage(import('./pages/admin/inspiration-hub')),
          loader: loadAdminInspirationHubPageData,
        }]
      },
      {
        path: '*',
        loader({ request }) {
          const message = new URL(request.url).searchParams.get('message')
          if (message) {
            throw new Response(null, { statusText: message })
          }
          throw new Response(null, { status: 404 })
        }
      }]
    }]
  }
]);

root.render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorker.register();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
